import { createFileRoute } from '@tanstack/react-router';
import {
    ArrowDown,
    ArrowUp,
    Download,
    FileText,
    GripVertical,
    Image,
    LineChart,
    Loader2,
    Palette,
    Plus,
    RotateCcw,
    Upload,
    X,
} from 'lucide-react';
import { type DragEvent, useEffect, useMemo, useRef, useState } from 'react';

import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { type ReportAssumptions } from '@apps/web/components/admin/report_generator/report_calculations';
import {
    REPORT_THEMES,
    type ReportCustomization,
    ReportDocument,
    type ReportStyleOptions,
} from '@apps/web/components/admin/report_generator/report_document';
import { ReportHtmlDocument } from '@apps/web/components/admin/report_generator/report_html_document';
import {
    createDefaultReportLayout,
    getReportMetricDefinition,
    getSelectedReportMetricIds,
    REPORT_CHARTS,
    REPORT_METRIC_BY_ID,
    REPORT_METRIC_CATEGORIES,
    REPORT_METRICS,
    type ReportChartId,
    type ReportLayout,
    type ReportMetricId,
    type ReportSectionLayout,
} from '@apps/web/components/admin/report_generator/report_metrics';
import { Button } from '@apps/web/components/ui/button';
import { Card, CardContent } from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    DateRangePicker,
    type DateRangePickerValue,
} from '@apps/web/components/ui/date_range_picker';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    getDateStringInTimezone,
    shiftDateKey,
} from '@apps/web/lib/date_utils';
import { triggerBlobDownload } from '@apps/web/lib/tabular_export';
import { cn } from '@apps/web/lib/utils';
import { useAdminOrganizationReport } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/report-generator')({
    head: () => ({ meta: [{ title: 'Report Generator | Phoebe Admin' }] }),
    component: ReportGeneratorPage,
});

const DEFAULT_ASSUMPTIONS: ReportAssumptions = {
    billingRateUsd: 32,
    monthlyFeeUsd: 2680,
};

const DEFAULT_CUSTOMIZATION: ReportCustomization = {
    title: '',
    note: '',
};

const DEFAULT_STYLE_OPTIONS: ReportStyleOptions = {
    showFlowers: true,
    showSectionDividers: true,
    blackAndWhite: false,
    themeId: 'phoebe',
    logoSrc: '',
    chartIds: REPORT_CHARTS.map((chart) => chart.id),
};

type GeneratedReportRequest = {
    readonly organizationId: string;
    readonly dateRange: DateRangePickerValue;
    readonly assumptions: ReportAssumptions;
    readonly layout: ReportLayout;
    readonly customization: ReportCustomization;
    readonly styleOptions: ReportStyleOptions;
};

type DraggedMetric = {
    readonly metricId: ReportMetricId;
    readonly sourceSectionId: string | null;
};

// The picker needs a timezone to compute "today"; the browser zone is fine
// for an internal default — the API interprets the dates in the org's zone.
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const DEFAULT_LAYOUT = createDefaultReportLayout();

function defaultDateRange(): DateRangePickerValue {
    const yesterday = shiftDateKey(
        getDateStringInTimezone(new Date(), BROWSER_TIMEZONE),
        -1,
    );
    return { from: shiftDateKey(yesterday, -29), to: yesterday };
}

function AssumptionInput({
    label,
    value,
    onChange,
}: {
    readonly label: string;
    readonly value: number;
    readonly onChange: (value: number) => void;
}) {
    return (
        <div className="space-y-1">
            <Label className="text-sm font-medium">{label}</Label>
            <Input
                type="number"
                min={0}
                value={value}
                onChange={(event) => {
                    const parsed = Number(event.target.value);
                    onChange(Number.isFinite(parsed) ? parsed : 0);
                }}
            />
        </div>
    );
}

function StyleOptionCheckbox({
    label,
    checked,
    onChange,
}: {
    readonly label: string;
    readonly checked: boolean;
    readonly onChange: (checked: boolean) => void;
}) {
    return (
        <label className="hover:bg-muted/70 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm">
            <Checkbox
                checked={checked}
                onCheckedChange={(value) => onChange(value === true)}
            />
            <span>{label}</span>
        </label>
    );
}

function ThemePicker({
    value,
    onChange,
}: {
    readonly value: ReportStyleOptions['themeId'];
    readonly onChange: (themeId: ReportStyleOptions['themeId']) => void;
}) {
    return (
        <div className="grid grid-cols-4 gap-1">
            {REPORT_THEMES.map((theme) => (
                <button
                    key={theme.id}
                    type="button"
                    onClick={() => onChange(theme.id)}
                    className={cn(
                        'hover:bg-muted/70 flex h-9 items-center gap-2 rounded-md border px-2 text-left text-xs font-medium transition-colors',
                        value === theme.id
                            ? 'border-primary bg-muted'
                            : 'border-transparent',
                    )}
                >
                    <span
                        className="size-3 rounded-full"
                        style={{ backgroundColor: theme.accent }}
                    />
                    <span className="min-w-0 truncate">{theme.label}</span>
                </button>
            ))}
        </div>
    );
}

function moveItem<T>(items: ReadonlyArray<T>, index: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= items.length) {
        return items;
    }
    const nextItems = [...items];
    const [item] = nextItems.splice(index, 1);
    if (item === undefined) {
        return items;
    }
    nextItems.splice(toIndex, 0, item);
    return nextItems;
}

function defaultSectionIdForMetric(metricId: ReportMetricId): string {
    return (
        DEFAULT_LAYOUT.sections.find((section) =>
            section.metricIds.includes(metricId),
        )?.id ?? 'headline'
    );
}

function removeMetricFromLayout(
    layout: ReportLayout,
    metricId: ReportMetricId,
): ReportLayout {
    return {
        sections: layout.sections.map((section) => ({
            ...section,
            metricIds: section.metricIds.filter((id) => id !== metricId),
        })),
    };
}

function insertMetricIntoSection({
    layout,
    metricId,
    sectionId,
    targetMetricId,
}: {
    readonly layout: ReportLayout;
    readonly metricId: ReportMetricId;
    readonly sectionId: string;
    readonly targetMetricId?: ReportMetricId;
}): ReportLayout {
    const layoutWithoutMetric = removeMetricFromLayout(layout, metricId);
    return {
        sections: layoutWithoutMetric.sections.map((section) => {
            if (section.id !== sectionId) {
                return section;
            }
            const metricIds = [...section.metricIds];
            const targetIndex = targetMetricId
                ? metricIds.indexOf(targetMetricId)
                : -1;
            metricIds.splice(
                targetIndex === -1 ? metricIds.length : targetIndex,
                0,
                metricId,
            );
            return { ...section, metricIds };
        }),
    };
}

function readDraggedMetric(
    event: DragEvent<HTMLElement>,
): DraggedMetric | null {
    const metricId = event.dataTransfer.getData('application/x-report-metric');
    if (!REPORT_METRIC_BY_ID.has(metricId as ReportMetricId)) {
        return null;
    }
    const sourceSectionId =
        event.dataTransfer.getData('application/x-report-section') || null;
    return {
        metricId: metricId as ReportMetricId,
        sourceSectionId,
    };
}

function reportMatchesRequest(
    report: ReturnType<typeof useAdminOrganizationReport>['data'],
    request: GeneratedReportRequest | null,
): boolean {
    return (
        !!report &&
        !!request &&
        report.organization_id === request.organizationId &&
        report.start_date === request.dateRange.from &&
        report.end_date === request.dateRange.to
    );
}

function LayoutEditor({
    layout,
    onChange,
}: {
    readonly layout: ReportLayout;
    readonly onChange: (layout: ReportLayout) => void;
}) {
    const [draggedMetric, setDraggedMetric] = useState<DraggedMetric | null>(
        null,
    );
    const [metricSearch, setMetricSearch] = useState('');
    const selectedMetricIds = getSelectedReportMetricIds(layout);
    const unselectedMetrics = useMemo(
        () =>
            REPORT_METRICS.filter(
                (metric) => !selectedMetricIds.has(metric.id),
            ),
        [selectedMetricIds],
    );
    const normalizedSearch = metricSearch.trim().toLowerCase();
    const visibleMetrics = unselectedMetrics.filter((metric) =>
        metric.label.toLowerCase().includes(normalizedSearch),
    );

    const updateSection = (
        sectionId: string,
        updater: (section: ReportSectionLayout) => ReportSectionLayout,
    ) => {
        onChange({
            sections: layout.sections.map((section) =>
                section.id === sectionId ? updater(section) : section,
            ),
        });
    };

    const moveSection = (sectionIndex: number, direction: -1 | 1) => {
        onChange({
            sections: moveItem(
                layout.sections,
                sectionIndex,
                sectionIndex + direction,
            ),
        });
    };

    const startMetricDrag = (
        event: DragEvent<HTMLElement>,
        metricId: ReportMetricId,
        sourceSectionId: string | null,
    ) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('application/x-report-metric', metricId);
        event.dataTransfer.setData(
            'application/x-report-section',
            sourceSectionId ?? '',
        );
        setDraggedMetric({ metricId, sourceSectionId });
    };

    const handleSectionDrop = (
        event: DragEvent<HTMLElement>,
        sectionId: string,
        targetMetricId?: ReportMetricId,
    ) => {
        event.preventDefault();
        const droppedMetric = readDraggedMetric(event);
        setDraggedMetric(null);
        if (!droppedMetric) {
            return;
        }
        onChange(
            insertMetricIntoSection({
                layout,
                metricId: droppedMetric.metricId,
                sectionId,
                targetMetricId,
            }),
        );
    };

    const removeMetric = (metricId: ReportMetricId) => {
        onChange(removeMetricFromLayout(layout, metricId));
    };

    const addMetricToDefaultSection = (metricId: ReportMetricId) => {
        onChange(
            insertMetricIntoSection({
                layout,
                metricId,
                sectionId: defaultSectionIdForMetric(metricId),
            }),
        );
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <Label className="text-sm font-medium">Report layout</Label>
                    <div className="text-muted-foreground text-xs">
                        {selectedMetricIds.size} selected
                    </div>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onChange(createDefaultReportLayout())}
                    className="h-8 px-2"
                >
                    <RotateCcw className="mr-1 size-3.5" />
                    Reset
                </Button>
            </div>
            <div className="grid grid-cols-[250px_minmax(0,1fr)] gap-3">
                <div className="space-y-2">
                    <Label className="text-xs font-medium tracking-wide text-slate-500 uppercase">
                        Metric library
                    </Label>
                    <Input
                        value={metricSearch}
                        placeholder="Search metrics…"
                        className="h-8 text-xs"
                        onChange={(event) =>
                            setMetricSearch(event.target.value)
                        }
                    />
                    <div className="space-y-3">
                        {REPORT_METRIC_CATEGORIES.map((category) => {
                            const categoryMetrics = visibleMetrics.filter(
                                (metric) => metric.category === category.id,
                            );
                            if (categoryMetrics.length === 0) {
                                return null;
                            }
                            return (
                                <div key={category.id} className="space-y-1">
                                    <div className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                                        {category.label}
                                    </div>
                                    {categoryMetrics.map((metric) => (
                                        <div
                                            key={metric.id}
                                            title={metric.description}
                                            draggable={true}
                                            onDragStart={(event) =>
                                                startMetricDrag(
                                                    event,
                                                    metric.id,
                                                    null,
                                                )
                                            }
                                            onDragEnd={() =>
                                                setDraggedMetric(null)
                                            }
                                            className={cn(
                                                'group hover:bg-muted/70 flex h-7 cursor-grab items-center gap-1 rounded-md border bg-white px-1.5 text-[11px] transition-colors active:cursor-grabbing',
                                                draggedMetric?.metricId ===
                                                    metric.id && 'opacity-50',
                                            )}
                                        >
                                            <GripVertical className="text-muted-foreground size-3 shrink-0" />
                                            <span className="min-w-0 flex-1 truncate font-medium">
                                                {metric.label}
                                            </span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                                                aria-label={`Add ${metric.label}`}
                                                onClick={() =>
                                                    addMetricToDefaultSection(
                                                        metric.id,
                                                    )
                                                }
                                            >
                                                <Plus className="size-2.5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                        {visibleMetrics.length === 0 ? (
                            <div className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
                                {unselectedMetrics.length === 0
                                    ? 'All metrics selected'
                                    : 'No metrics match your search'}
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="min-w-0 space-y-3">
                    <Label className="text-xs font-medium tracking-wide text-slate-500 uppercase">
                        Sections
                    </Label>
                    {layout.sections.map((section, sectionIndex) => (
                        <div
                            key={section.id}
                            onDragOver={(event) => {
                                event.preventDefault();
                                event.dataTransfer.dropEffect = 'move';
                            }}
                            onDrop={(event) =>
                                handleSectionDrop(event, section.id)
                            }
                            className="space-y-3 rounded-md border bg-white p-3 shadow-xs"
                        >
                            <div className="flex items-start gap-2">
                                <div className="min-w-0 flex-1 space-y-2">
                                    <Input
                                        value={section.title}
                                        onChange={(event) =>
                                            updateSection(
                                                section.id,
                                                (current) => ({
                                                    ...current,
                                                    title: event.target.value,
                                                }),
                                            )
                                        }
                                    />
                                    <Textarea
                                        value={section.note}
                                        placeholder="Section note"
                                        className="min-h-16 text-sm"
                                        onChange={(event) =>
                                            updateSection(
                                                section.id,
                                                (current) => ({
                                                    ...current,
                                                    note: event.target.value,
                                                }),
                                            )
                                        }
                                    />
                                </div>
                                <div className="flex shrink-0 flex-col gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-7"
                                        disabled={sectionIndex === 0}
                                        aria-label={`Move ${section.title} up`}
                                        onClick={() =>
                                            moveSection(sectionIndex, -1)
                                        }
                                    >
                                        <ArrowUp className="size-3.5" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-7"
                                        disabled={
                                            sectionIndex ===
                                            layout.sections.length - 1
                                        }
                                        aria-label={`Move ${section.title} down`}
                                        onClick={() =>
                                            moveSection(sectionIndex, 1)
                                        }
                                    >
                                        <ArrowDown className="size-3.5" />
                                    </Button>
                                </div>
                            </div>
                            <div className="flex min-h-10 flex-wrap gap-1 rounded-md border border-dashed p-2">
                                {section.metricIds.length === 0 ? (
                                    <div className="text-muted-foreground flex h-6 items-center text-xs">
                                        Drag metric pills here
                                    </div>
                                ) : (
                                    section.metricIds.map((metricId) => {
                                        const metric =
                                            getReportMetricDefinition(metricId);
                                        return (
                                            <div
                                                key={metricId}
                                                draggable={true}
                                                onDragStart={(event) =>
                                                    startMetricDrag(
                                                        event,
                                                        metricId,
                                                        section.id,
                                                    )
                                                }
                                                onDragEnd={() =>
                                                    setDraggedMetric(null)
                                                }
                                                onDragOver={(event) => {
                                                    event.preventDefault();
                                                    event.dataTransfer.dropEffect =
                                                        'move';
                                                }}
                                                onDrop={(event) => {
                                                    event.stopPropagation();
                                                    handleSectionDrop(
                                                        event,
                                                        section.id,
                                                        metricId,
                                                    );
                                                }}
                                                title={metric.description}
                                                className={cn(
                                                    'bg-muted/60 hover:bg-muted inline-flex h-7 max-w-[190px] cursor-grab items-center gap-1 rounded-full px-2 text-[11px] transition-colors active:cursor-grabbing',
                                                    draggedMetric?.metricId ===
                                                        metricId &&
                                                        'opacity-50',
                                                )}
                                            >
                                                <GripVertical className="text-muted-foreground size-3 shrink-0" />
                                                <span className="min-w-0 truncate font-medium">
                                                    {metric.label}
                                                </span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="size-4 shrink-0 rounded-full"
                                                    aria-label={`Remove ${metric.label}`}
                                                    onClick={() =>
                                                        removeMetric(metricId)
                                                    }
                                                >
                                                    <X className="size-2.5" />
                                                </Button>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ReportGeneratorPage() {
    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
    const [dateRange, setDateRange] =
        useState<DateRangePickerValue>(defaultDateRange);
    // Whether the admin picked the range by hand; hands-off ranges may be
    // clamped to the org's live window below.
    const [rangeTouched, setRangeTouched] = useState(false);
    const [assumptions, setAssumptions] =
        useState<ReportAssumptions>(DEFAULT_ASSUMPTIONS);
    const [layout, setLayout] = useState<ReportLayout>(
        createDefaultReportLayout,
    );
    const [customization, setCustomization] = useState<ReportCustomization>(
        DEFAULT_CUSTOMIZATION,
    );
    const [styleOptions, setStyleOptions] = useState<ReportStyleOptions>(
        DEFAULT_STYLE_OPTIONS,
    );
    const [isDownloading, setIsDownloading] = useState(false);
    const [generatedRequest, setGeneratedRequest] =
        useState<GeneratedReportRequest | null>(null);

    const {
        data: report,
        isLoading,
        error,
    } = useAdminOrganizationReport(
        generatedRequest?.organizationId ?? null,
        generatedRequest?.dateRange.from ?? dateRange.from,
        generatedRequest?.dateRange.to ?? dateRange.to,
        true,
    );

    // Clamp the default period to the org's go-live date so an org that has
    // been live for 12 days gets a 12-day report, not a "last 30 days"
    // framing. A hand-picked range is never overridden. Date keys compare
    // lexicographically.
    const customerLiveDate = report?.customer_live_date;
    useEffect(() => {
        if (
            !customerLiveDate ||
            rangeTouched ||
            customerLiveDate <= dateRange.from
        ) {
            return;
        }
        setDateRange({
            from:
                customerLiveDate < dateRange.to
                    ? customerLiveDate
                    : dateRange.to,
            to: dateRange.to,
        });
        setGeneratedRequest(null);
    }, [customerLiveDate, dateRange.from, dateRange.to, rangeTouched]);

    const generatedReportMatchesRequest = reportMatchesRequest(
        report,
        generatedRequest,
    );
    const generatedReportIsPending =
        !!generatedRequest &&
        !error &&
        (isLoading || !generatedReportMatchesRequest);

    const handleGenerateReport = () => {
        if (!selectedOrgId) {
            return;
        }
        setGeneratedRequest({
            organizationId: selectedOrgId,
            dateRange,
            assumptions,
            layout,
            customization,
            styleOptions,
        });
    };

    const handleLogoUpload = (file: File | undefined) => {
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            const result = reader.result;
            if (typeof result !== 'string') {
                return;
            }
            setStyleOptions((current) => ({
                ...current,
                logoSrc: result,
            }));
        });
        reader.readAsDataURL(file);
    };

    const toggleChart = (chartId: ReportChartId, checked: boolean) => {
        setStyleOptions((current) => ({
            ...current,
            chartIds: checked
                ? [...new Set([...current.chartIds, chartId])]
                : current.chartIds.filter((id) => id !== chartId),
        }));
    };

    const handleDownload = async () => {
        if (!report || !generatedRequest || !generatedReportMatchesRequest) {
            return;
        }
        setIsDownloading(true);
        try {
            const { pdf } = await import('@react-pdf/renderer');
            const blob = await pdf(
                <ReportDocument
                    report={report}
                    assumptions={generatedRequest.assumptions}
                    layout={generatedRequest.layout}
                    customization={generatedRequest.customization}
                    styleOptions={generatedRequest.styleOptions}
                />,
            ).toBlob();
            const orgSlug = report.organization_name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            triggerBlobDownload(
                blob,
                `phoebe-report-${orgSlug}-${report.start_date}-${report.end_date}.pdf`,
            );
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="flex h-full gap-6 overflow-hidden p-6">
            <div className="flex w-[720px] shrink-0 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
                    <div>
                        <h2 className="text-lg font-semibold">
                            Report Generator
                        </h2>
                        <p className="text-muted-foreground text-sm">
                            Generate a customer-facing Phoebe report
                        </p>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-sm font-medium">
                            Organization
                        </Label>
                        <OrganizationCombobox
                            value={selectedOrgId ? [selectedOrgId] : []}
                            onValueChange={(ids) => {
                                setSelectedOrgId(
                                    ids.find((id) => id !== selectedOrgId) ??
                                        null,
                                );
                                setDateRange(defaultDateRange());
                                setRangeTouched(false);
                                setCustomization(DEFAULT_CUSTOMIZATION);
                                setStyleOptions(DEFAULT_STYLE_OPTIONS);
                                setGeneratedRequest(null);
                            }}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-sm font-medium">
                            Date range
                        </Label>
                        <DateRangePicker
                            value={dateRange}
                            timezone={BROWSER_TIMEZONE}
                            onChange={(range) => {
                                setRangeTouched(true);
                                setDateRange(range);
                                setGeneratedRequest(null);
                            }}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">
                            Report copy
                        </Label>
                        <Input
                            value={customization.title}
                            placeholder="Custom report title"
                            onChange={(event) =>
                                setCustomization({
                                    ...customization,
                                    title: event.target.value,
                                })
                            }
                        />
                        <Textarea
                            value={customization.note}
                            placeholder="Optional note under the date range"
                            className="min-h-20"
                            onChange={(event) =>
                                setCustomization({
                                    ...customization,
                                    note: event.target.value,
                                })
                            }
                        />
                    </div>
                    <LayoutEditor layout={layout} onChange={setLayout} />
                    <AssumptionInput
                        label="Billing rate ($/hr)"
                        value={assumptions.billingRateUsd}
                        onChange={(billingRateUsd) =>
                            setAssumptions({ ...assumptions, billingRateUsd })
                        }
                    />
                    <AssumptionInput
                        label="Monthly Phoebe fee ($)"
                        value={assumptions.monthlyFeeUsd}
                        onChange={(monthlyFeeUsd) =>
                            setAssumptions({ ...assumptions, monthlyFeeUsd })
                        }
                    />
                </div>
                <div className="bg-background border-t pt-4">
                    <div className="space-y-3 pr-2">
                        <div>
                            <div className="flex items-center gap-2">
                                <Palette className="text-muted-foreground size-4" />
                                <Label className="text-sm font-medium">
                                    Report style
                                </Label>
                            </div>
                            <div className="mt-2 space-y-3">
                                <div className="space-y-1">
                                    <Label className="text-muted-foreground text-xs">
                                        Theme
                                    </Label>
                                    <ThemePicker
                                        value={styleOptions.themeId}
                                        onChange={(themeId) =>
                                            setStyleOptions({
                                                ...styleOptions,
                                                themeId,
                                            })
                                        }
                                    />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Image className="text-muted-foreground size-3.5" />
                                        <Label className="text-muted-foreground text-xs">
                                            Customer logo
                                        </Label>
                                    </div>
                                    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-1">
                                        <Input
                                            value={
                                                styleOptions.logoSrc.startsWith(
                                                    'data:',
                                                )
                                                    ? 'Uploaded logo'
                                                    : styleOptions.logoSrc
                                            }
                                            placeholder="Logo URL or upload"
                                            className="h-8 text-xs"
                                            onChange={(event) =>
                                                setStyleOptions({
                                                    ...styleOptions,
                                                    logoSrc: event.target.value,
                                                })
                                            }
                                            disabled={styleOptions.logoSrc.startsWith(
                                                'data:',
                                            )}
                                        />
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            className="hidden"
                                            onChange={(event) =>
                                                handleLogoUpload(
                                                    event.target.files?.[0],
                                                )
                                            }
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-2"
                                            onClick={() =>
                                                logoInputRef.current?.click()
                                            }
                                        >
                                            <Upload className="mr-1 size-3.5" />
                                            Upload
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-2"
                                            disabled={!styleOptions.logoSrc}
                                            onClick={() => {
                                                if (logoInputRef.current) {
                                                    logoInputRef.current.value =
                                                        '';
                                                }
                                                setStyleOptions({
                                                    ...styleOptions,
                                                    logoSrc: '',
                                                });
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                    <StyleOptionCheckbox
                                        label="Show flowers"
                                        checked={styleOptions.showFlowers}
                                        onChange={(showFlowers) =>
                                            setStyleOptions({
                                                ...styleOptions,
                                                showFlowers,
                                            })
                                        }
                                    />
                                    <StyleOptionCheckbox
                                        label="Section lines"
                                        checked={
                                            styleOptions.showSectionDividers
                                        }
                                        onChange={(showSectionDividers) =>
                                            setStyleOptions({
                                                ...styleOptions,
                                                showSectionDividers,
                                            })
                                        }
                                    />
                                    <StyleOptionCheckbox
                                        label="Black and white"
                                        checked={styleOptions.blackAndWhite}
                                        onChange={(blackAndWhite) =>
                                            setStyleOptions({
                                                ...styleOptions,
                                                blackAndWhite,
                                            })
                                        }
                                    />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <LineChart className="text-muted-foreground size-3.5" />
                                        <Label className="text-muted-foreground text-xs">
                                            Charts
                                        </Label>
                                    </div>
                                    <div className="grid grid-cols-3 gap-1">
                                        {REPORT_CHARTS.map((chart) => (
                                            <StyleOptionCheckbox
                                                key={chart.id}
                                                label={chart.title}
                                                checked={styleOptions.chartIds.includes(
                                                    chart.id,
                                                )}
                                                onChange={(checked) =>
                                                    toggleChart(
                                                        chart.id,
                                                        checked,
                                                    )
                                                }
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                            <Button
                                onClick={handleGenerateReport}
                                disabled={
                                    !selectedOrgId || generatedReportIsPending
                                }
                            >
                                {generatedReportIsPending ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <FileText className="mr-2 h-4 w-4" />
                                )}
                                Generate report
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleDownload}
                                disabled={
                                    !report ||
                                    !generatedRequest ||
                                    generatedReportIsPending ||
                                    isDownloading
                                }
                            >
                                {isDownloading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="mr-2 h-4 w-4" />
                                )}
                                Download PDF
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="min-w-0 flex-1">
                <ReportPreview
                    report={report}
                    request={generatedRequest}
                    isLoading={generatedReportIsPending}
                    hasOrganization={!!selectedOrgId}
                    hasError={!!error}
                />
            </div>
        </div>
    );
}

function ReportPreview({
    report,
    request,
    isLoading,
    hasOrganization,
    hasError,
}: {
    readonly report: ReturnType<typeof useAdminOrganizationReport>['data'];
    readonly request: GeneratedReportRequest | null;
    readonly isLoading: boolean;
    readonly hasOrganization: boolean;
    readonly hasError: boolean;
}) {
    if (!hasOrganization) {
        return (
            <Card className="flex h-full items-center justify-center">
                <CardContent className="text-muted-foreground text-sm">
                    Select an organization to generate a report
                </CardContent>
            </Card>
        );
    }
    if (!request) {
        return (
            <Card className="flex h-full items-center justify-center">
                <CardContent className="text-muted-foreground text-sm">
                    No report generated yet
                </CardContent>
            </Card>
        );
    }
    if (hasError) {
        return (
            <Card className="flex h-full items-center justify-center">
                <CardContent className="text-destructive text-sm">
                    Failed to load report data
                </CardContent>
            </Card>
        );
    }
    if (isLoading || !report) {
        return (
            <Card className="flex h-full items-center justify-center">
                <CardContent className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating report…
                </CardContent>
            </Card>
        );
    }
    return (
        <div className="bg-admin-sunken h-full overflow-auto rounded-lg border px-6 py-8">
            <ReportHtmlDocument
                report={report}
                assumptions={request.assumptions}
                layout={request.layout}
                customization={request.customization}
                styleOptions={request.styleOptions}
            />
        </div>
    );
}
